import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TrainingTemplateService } from '../../../services/training-template.service';
import { OrganisationUnitService } from '../../../services/organisation-unit.service';
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { OrganisationUnitModel } from '../../../models/organisation-unit/organisation-unit.model';
import { first, shareReplay, switchMap } from 'rxjs/operators';
import { OrganisationUnitTypeModel } from '../../../models/organisation-unit/organisation-unit-type.model';
import { TrainingGroupModel } from '../../../models/training-group.model';
import { PersonService } from '../../../services/person.service';
import { TrainingGroupService } from '../../../services/training-group.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoadingService } from '../../../services/loading.service';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { FileUtilService } from '../../../services/file-util.service';
import { TrainingTypeModel } from '../../../models/training/training-type.model';
import { TrainingTemplateSummaryModel } from '../../../models/training-template/training-template-summary.model';
import { TrainingService } from '../../../services/training.service';
import { TrainingCreationAndUpdateModel } from '../../../models/training/training-creation-and-update.model';
import { TrainingLocationTypeModel } from '../../../models/training/training-location-type.model';
import { GoogleMapsService } from '../../../services/google-maps.service';
import { TrainingDateSelectionComponent } from '../../../components/training-date-selection/training-date-selection.component';
import { TrainingFullModel } from '../../../models/training/training-full.model';
import { TimeService } from '../../../services/time.service';
import { TrainingHelperService } from '../../../services/training-helper.service';
import {
  PrimaryButtonType,
  SecondaryButtonType,
} from '../../../models/button-type.model';
import { DxDropDownButtonTypes } from 'devextreme-angular/ui/drop-down-button';
import { TranslateService } from '@ngx-translate/core';
import { EventMemberModel } from '../../../models/events/event-member.model';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { IntegrationsService } from '../../../services/integration/integrations.service';
import { IntegrationModel } from '../../../models/integration/integration.model';
import { IntegrationTypeModel } from '../../../models/integration/integration-type.model';
import { CompletedEventService } from '../../../services/completed-event.service';
import { TrainingCategoryModel } from '../../../models/training-category/training-category.model';
import { TrainingCategoryService } from '../../../services/training-categories/training-category.service';
import { UserService } from '../../../services/user.service';
import validationEngine from 'devextreme/ui/validation_engine';
import { ExternalContactService } from 'src/app/services/external-contacts.service';
import {
  DxDropDownBoxComponent,
} from 'devextreme-angular';
import { TrainingEmployeePopupComponent } from '../../../components/training-employee-popup/training-employee-popup.component';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { PersonModel } from 'src/app/models/person/person.model';
import { IDD_MIN_TRAINING_DURATION_MINUTES } from 'src/app/constants/idd.constants';
import { TenantSubscriptionService } from 'src/app/services/tenants/tenant-subscription.service';
import { EmployeeStatusTabsModel } from 'src/app/models/employee-status.model';
import { TrainingChannelStatisticsModel } from 'src/app/models/training/training-channel-statistics.model';
import { EventMemberStatusModel } from 'src/app/models/events/event-member-status.model';
import { Clipboard } from '@angular/cdk/clipboard';
import { ParticipationQuestionTemplateService } from 'src/app/services/participation-question-template.service';
import { EventParticipationQuestionTemplateModel } from 'src/app/models/event-participations/event-participation-question-template.model';
import { EditTrainingParticipationQuestionsComponent } from 'src/app/components/edit-training-participation-questions/edit-training-participation-questions.component';
import { TrainingReminderTypeModel } from 'src/app/models/training/training-reminder-type.model';
import { TrainingStartDateChangedNotificationResult } from 'src/app/components/training-start-date-changed-notification-dialog/training-start-date-changed-notification-dialog.component';
import { DateTime } from 'luxon';
import { ReportPreviewService } from 'src/app/services/report-preview.service';
import { ReportKeyTypeModel } from 'src/app/models/report-preview/report-key-type.model';
import { ReportKeyModel } from 'src/app/models/report-preview/report-key.model';
import { PreviewResponse } from 'src/app/models/report-preview/preview-response.model';
import { TrainingStatusModel } from 'src/app/models/training/training-status-model';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { UserContextService } from 'src/app/services/user-context.service';
import { StandardTrainingCategoryViewModel } from 'src/app/models/standard-training-category/standard-training-category-view.model';
import { StandardTrainingCategoryService } from 'src/app/services/training-categories/standard-training-category.service';
import { PublishingMode } from 'src/app/models/training/publishing-mode.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { RoleModel } from 'src/app/models/person/role.model';
import { RoleService } from 'src/app/services/role.service';
import { SubscriptionService } from 'src/app/services/subscriptions/subscription.service';
import { TenantSubscriberModel } from 'src/app/models/subscription/tenant-subscriber.model';
import { EventVisibilityModeModel } from 'src/app/models/event-visibility-mode.model';
import { TenantContextService } from 'src/app/services/tenant-context.service';

interface SelectOption {
  id: string;
  name: string;
  selected?: boolean;
}

@Component({
  selector: 'app-training-form',
  templateUrl: './training-form.component.html',
  styleUrls: ['./training-form.component.scss'],
  standalone: false
})
export class TrainingFormComponent implements OnInit {
  @ViewChild('descriptionForm', { static: true })
  descriptionForm!: DxFormComponent;
  @ViewChild('settingsForm', { static: true }) settingsForm!: DxFormComponent;
  @ViewChild('locationForm', { static: true }) locationForm!: DxFormComponent;
  @ViewChild('trainingDateSelection')
  trainingDateSelectionComponent!: TrainingDateSelectionComponent;
  @ViewChild(DxDropDownBoxComponent, { static: false })
  templateDropDown!: DxDropDownBoxComponent;
  private googleAutocomplete!: ElementRef<HTMLElement>;
  @ViewChild('googleAutocomplete') set googleAutocompleteContent(
    content: ElementRef<HTMLElement>
  ) {
    if (content) {
      this.googleAutocomplete = content;
    }
  }
  private timePicker!: ElementRef<HTMLElement>;
  @ViewChild('timePicker') set timePickerContent(
    content: ElementRef<HTMLElement>
  ) {
    if (content) {
      this.timePicker = content;
    }
  }
  @ViewChild('trainingEmployeeModal')
  trainingEmployeeModal!: TrainingEmployeePopupComponent;

  @ViewChild('participationQuestionEditorComponent')
  participationQuestionEditorComponent!: EditTrainingParticipationQuestionsComponent;

  @ViewChild('standardCategoryDropDown', { static: false })
  standardCategoryDropDown!: DxDropDownBoxComponent;

  private googlePlaceinputChanged = new Subject<string>();
  googleAutocompleteResults$ = this.googlePlaceinputChanged.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap((searchTerm) => this.mapsService.getAutocomplete(searchTerm))
  );

  private subs = new Subscription();

  get channelMembersCount() {
    return this.allAcceptedMembersCount + this.allDeclinedMembersCount;
  }

  public get isIntegrationLocationType(): boolean {
    const locationType = this.trainingForm.trainingLocation.locationType;

    return (
      locationType !== TrainingLocationTypeModel.Address &&
      locationType !== TrainingLocationTypeModel.Web &&
      locationType !== TrainingLocationTypeModel.ExternalEvent
    );
  }

  training: TrainingFullModel | null = null;
  displayEventKey: boolean = false;
  isWbdConfigured: boolean = false;
  isViewMode: boolean = false;
  isEmployeePopupVisible: boolean = false;
  employeePopupTab: number = 0;
  allMembers!: EventMemberModel[];
  openMembers: EventMemberModel[] = [];
  acceptedMembers: EventMemberModel[] = [];
  declinedMembers: EventMemberModel[] = [];
  requestedMembers: EventMemberModel[] = [];
  queueMembers: EventMemberModel[] = [];
  completedMembers: EventMemberModel[] = [];
  channels: TrainingChannelStatisticsModel[] = [];
  googlePlaces: any[] = [];
  googleSearchText: string = '';
  TrainingLocationType = TrainingLocationTypeModel;
  TrainingTypeModel = TrainingTypeModel;
  editingTrainingId!: string;
  organizationUnits!: OrganisationUnitModel[];
  certificateOrganizationUnits!: OrganisationUnitModel[];
  trainingGroups!: TrainingGroupModel[];
  trainingCategories!: TrainingCategoryModel[];
  standardTrainingCategories!: StandardTrainingCategoryViewModel[];
  usedStandardTrainingCategories!: StandardTrainingCategoryViewModel[];
  roles!: RoleModel[];
  subscribers!: TenantSubscriberModel[];
  templates!: TrainingTemplateSummaryModel[];
  questionTemplates: EventParticipationQuestionTemplateModel[] = [];
  locations!: SelectOption[];
  departments!: SelectOption[];
  trainers!: SelectOption[];
  trainingTypeOptions!: DxSelectBoxTypes.Properties;
  learningTypeOptions!: DxSelectBoxTypes.Properties;
  learningContentOptions!: DxSelectBoxTypes.Properties;
  trainingLocationTypeOptions!: DxSelectBoxTypes.Properties;
  primaryButton: PrimaryButtonType | null = null;
  buttonTypes: SecondaryButtonType[] | null = null;
  isDescriptionExpanded: boolean = false;
  isCertificateDescriptionExpanded: boolean = false;
  integrations: Array<any> = [];
  selectedDescriptionTab: number = 0;
  isTenantPublishedToSubscribers: boolean = false;
  isPrivatePublishing: boolean = false;
  personCount: number = 0;

  certificateFontFamily: string | null = null;
  certificateFontSize: string | null = null;

  showPreviewReportViewer = false;
  reportUrl: string = '';
   
  descriptionTabs: any[] = [
    {
      id: 0,
      text: this.translateService.instant('TRAINING.FORM.EXTENDED-DESCRIPTION'),
    },
    {
      id: 1,
      text: this.translateService.instant(
        'TRAINING.FORM.TEXT-FOR-CERTIFICATES'
      ),
    },
  ];

  reminderOptions: { value: TrainingReminderTypeModel; label: string; selected?: boolean; }[] = [
    { 
      value: TrainingReminderTypeModel.ConfirmationEmail, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.CONFIRMATION-EMAIL'
      ),
    },
    { 
      value: TrainingReminderTypeModel.OneWeekBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.1-WEEK-BEFORE'
      ),
    },
    { 
      value: TrainingReminderTypeModel.OneDayBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.1-DAY-BEFORE'
      ),
    },
    { 
      value: TrainingReminderTypeModel.ThirtyMinutesBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.30-MINUTES-BEFORE'
      ),
    },
    { 
      value: TrainingReminderTypeModel.FifteenMinutesBefore, 
      label: this.translateService.instant(
        'COMMON.FIELDS.TRAINING-REMINDER-OPTIONS.15-MINUTES-BEFORE'
      ),
    }
  ];


  menuActions: { value: SecondaryButtonType; name: string }[] = [
    {
      value: SecondaryButtonType.CopyLink,
      name: this.translateService.instant('TRAINING.FORM.COPY-INVITATION-LINK'),
    },
    {
      value: SecondaryButtonType.DownloadMemberList,
      name: this.translateService.instant('TRAINING.FORM.ATTENDANCE-LIST'),
    },
    {
      value: SecondaryButtonType.DownloadESignatureQRCode,
      name: this.translateService.instant('TRAINING.FORM.DOWNLOAD-ESIGNATURE-QR-CODE'),
    },
    {
      value: SecondaryButtonType.DownloadCompletedEventMemberList,
      name: this.translateService.instant('TRAINING.FORM.VIEW-ATTENDEES-LIST'),
    },
    {
      value: SecondaryButtonType.DownloadIcs,
      name: this.translateService.instant('TRAINING.FORM.EXPORT-CALENDAR'),
    },
    {
      value: SecondaryButtonType.SendInfo,
      name: this.translateService.instant('TRAINING.FORM.NOTE-PARTICIPANTS'),
    },
    {
      value: SecondaryButtonType.Edit,
      name: this.translateService.instant('TRAINING.FORM.EDIT-TRAINING'),
    },
    {
      value: SecondaryButtonType.Cancel,
      name: this.translateService.instant('TRAINING.FORM.CANCEL-TRAINING'),
    },
    {
      value: SecondaryButtonType.Delete,
      name: this.translateService.instant('TRAINING.FORM.DELETE-TRAINING'),
    },
    {
      value: SecondaryButtonType.AddAttendees,
      name: this.translateService.instant('TRAINING.FORM.ADD-ATTENDEES'),
    },
    {
      value: SecondaryButtonType.ActivityLog,
      name: this.translateService.instant('TRAINING.FORM.ACTIVITY-LOG'),
    },
    {
      value: SecondaryButtonType.CopyFeedbackLink,
      name: this.translateService.instant('TRAINING.FORM.COPY-FEEDBACK-LINK'),
    }
  ];

  trainingForm: TrainingCreationAndUpdateModel = {
    name: null,
    description: null,
    descriptionExtended: null,
    descriptionCertificate: null,
    trainerIds: [],
    trainingType: null,
    iddTimeMinutes: null,
    iddHours: 0,
    iddMinutes: 0,
    sendCertificateLink: true,
    sendFeedbackLink: true,
    organisationUnitLocationIds: [],
    trainingGroupIds: [],
    trainingCategoryIds: [],
    maximumMembers: null,
    trainingLocation: {
      locationType: null,
      location: null,
      suffix: null,
      placeId: null,
      calendarEventId: null,
      latitude: null,
      longitude: null,
    },
    templateId: null,
    emailBody: null,
    imageBase64: null,
    dates: [],
    lernart: null,
    lerninhalt: null,
    isDeployed: true,
    allowUnknownUsersToJoin: false,
    questions: null,
    certificateFooterLocationId: null,
    reminders: [],
    standardTrainingCategoryIds: [],
    publishingMode: PublishingMode.ByInvitationOnly,
    roleIds: [],
    subscriptionIds: [],
    visibilityMode: null,
    allowSharingWithSubscribers: false
  };

  selectedOrganizationUnitIds: Array<any> = [];
  modalConfirmationOptions: ConfirmationDialogData | null = null;
  showDeleteTrainingModal = false;
  showCancelTrainingModal = false;
  showCopyInvitationLinkModel = false;
  trainingStatusOption: string[];

  hasStartDateChanged = false;
  showStartDateChangedNotificationModal = false;
  startDateChangedNotificationResult: TrainingStartDateChangedNotificationResult | null = null;

  publishingModes = PublishingMode; 
  visibilityModes = EventVisibilityModeModel;

  isTrainingTimeValid: boolean = true;
  isTrainingLocationValid: boolean = true;
  trainingTimeAdapter = {
    getValue: () => {
      return this.trainingForm.iddHours! * 60 + this.trainingForm.iddMinutes!;
    },
  };
  trainingLocationAdapter = {
    getValue: () => {
      return this.googleSearchText;
    },
  };

  get locationValidationStatus() {
    return !this.isTrainingLocationValid ? 'invalid' : 'valid';
  }

  get getTrainingLocationLabel() {
    const isWeb =
      this.trainingForm.trainingLocation.locationType ===
        TrainingLocationTypeModel.Web;

    return isWeb
      ? this.translateService.instant('TRAINING.FORM.PLATFORM')
      : this.translateService.instant('COMMON.FIELDS.TRAINING-LOCATION');
  }

  get getTrainingLocationDetailsLabel() {
    const isWebOrExternalEvent =
      this.trainingForm.trainingLocation.locationType ===
        TrainingLocationTypeModel.Web ||
      this.trainingForm.trainingLocation.locationType ===
        TrainingLocationTypeModel.ExternalEvent;

    return isWebOrExternalEvent
      ? this.translateService.instant('TRAINING.FORM.LINK-OR-MEETING')
      : this.translateService.instant('COMMON.FIELDS.LOCATION-DETAILS');
  }

  get isExternalEvent() {
   return this.trainingForm.trainingLocation.locationType ===
      TrainingLocationTypeModel.ExternalEvent;
  }

  get primaryButtonText() {
    switch (this.primaryButton) {
      case PrimaryButtonType.AddAttendees:
        return this.translateService.instant('TRAINING.FORM.ADD-ATTENDEES');
      case PrimaryButtonType.CompleteTraining:
        return this.translateService.instant('TRAINING.FORM.CLOSE');
      case PrimaryButtonType.ShowFeedback:
        return this.translateService.instant('TRAINING.FORM.SHOW-FEEDBACK');
      case PrimaryButtonType.DownloadCompletedEventMemberList:
        return this.translateService.instant(
          'TRAINING.FORM.VIEW-ATTENDEES-LIST'
        );
      case PrimaryButtonType.DownloadMemberList:
        return this.translateService.instant('TRAINING.FORM.ATTENDANCE-LIST');
      case PrimaryButtonType.DownloadESignatureQRCode:
        return this.translateService.instant('TRAINING.FORM.DOWNLOAD-ESIGNATURE-QR-CODE');
      case PrimaryButtonType.Edit:
        return this.translateService.instant('TRAINING.FORM.EDIT-TRAINING');

      default:
        return this.translateService.instant('TRAINING.FORM.ADD-ATTENDEES');
    }
  }

  get allAcceptedMembersCount(): number {
    const allAcceptedAdditionalParticipants = this.acceptedMembers.reduce((sum, member) =>
      sum + (member.additionalParticipants?.length || 0), 0
    );
    return this.acceptedMembers.length + allAcceptedAdditionalParticipants;
  }

  get allDeclinedMembersCount(): number {
    const allDeclinedAdditionalParticipants = this.declinedMembers.reduce((sum, member) =>
      sum + (member.additionalParticipants?.length || 0), 0
    );
    return this.declinedMembers.length + allDeclinedAdditionalParticipants;
  }

  get allQueueMembersCount(): number {
    const allQueueAdditionalParticipants = this.queueMembers.reduce((sum, member) =>
      sum + (member.additionalParticipants?.length || 0), 0
    );
    return this.queueMembers.length + allQueueAdditionalParticipants;
  }

  get allRequestedMembersCount(): number {
    const allRequestedAdditionalParticipants = this.requestedMembers.reduce((sum, member) =>
      sum + (member.additionalParticipants?.length || 0), 0
    );
    return this.requestedMembers.length + allRequestedAdditionalParticipants;
  }

  get canShowRadioGroup(): boolean {
    if (!this.editingTrainingId) return true;

    return !!this.training &&
      this.training.status !== TrainingStatusModel.Completed  &&
      !this.training.isFromSubscribedSource;
  }

  get isVisibleToEveryone() {
    return this.isAllOrganizationUnitsSelected &&
      this.trainingForm.trainingGroupIds.length == 0 &&
      this.trainingForm.subscriptionIds.length == 0 &&
      this.trainingForm.roleIds.length == 0;
  }

  get isAllOrganizationUnitsSelected() {
    const selectedOrgUnitCount = 
      this.selectedOrganizationUnitIds.length;
      
    return selectedOrgUnitCount == 0
      || selectedOrgUnitCount == this.organizationUnits.length;
  }

  get allowUnknownUsersToJoinDisabled(): boolean {
    return !this.isViewMode && (
       (
        this.trainingForm.publishingMode === PublishingMode.PublishToSubscribers &&
        this.trainingForm.visibilityMode === EventVisibilityModeModel.Subscribers 
      ) ||
      (
        this.trainingForm.publishingMode === PublishingMode.DeployToTargetGroup &&
        this.trainingForm.allowSharingWithSubscribers
      )
    );
  }

  selectedEmployeeStatusTab: EmployeeStatusTabsModel | null = null;
  EmployeeStatusTabs = EmployeeStatusTabsModel;

  descriptionTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.ExtendedDescription;
  
  constructor(
    private organisationUnitService: OrganisationUnitService,
    private personService: PersonService,
    private externalContactService: ExternalContactService,
    private trainingGroupService: TrainingGroupService,
    private trainingCategoryService: TrainingCategoryService,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private router: Router,
    private loadingService: LoadingService,
    private fileUtilService: FileUtilService,
    private trainingTemplateService: TrainingTemplateService,
    private trainingService: TrainingService,
    readonly trainingHelperService: TrainingHelperService,
    private mapsService: GoogleMapsService,
    readonly translateService: TranslateService,
    private integrationsService: IntegrationsService,
    private completedEventService: CompletedEventService,
    private userService: UserService,
    private authorizationService: AuthorizationService,
    private tenantSubscriptionService: TenantSubscriptionService,
    private reportPreviewService: ReportPreviewService,
    private clipboard: Clipboard,
    private questionTemplateService: ParticipationQuestionTemplateService,
    public userContext: UserContextService,
    private standardTrainingCategoryService: StandardTrainingCategoryService,
    private pageStateService: PageStateService,
    private roleService: RoleService,
    private subscriptionService: SubscriptionService,
    private tenantContext: TenantContextService
  ) {
    this.trainingStatusOption = [
      this.translateService.instant('COMMON.EVENT-STATUS.DRAFT'),
      this.translateService.instant('COMMON.EVENT-STATUS.DEPLOYED'),
    ];
    this.autocompleteSearchFilter = this.autocompleteSearchFilter.bind(this);

    this.editingTrainingId = this.route.snapshot.params['id'];
    const path = this.route.snapshot.routeConfig!.path!;
    if (!path.includes('training/add') && !path.includes('training/edit')) {
      this.isViewMode = true;
    }

    this.route.queryParams.subscribe(params => {
      const tabKey = (params['employee-tab'] || '').toLowerCase();

      switch (tabKey) {
        case 'accepted':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.Accepted;
          break;

        case 'open':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.Open;
          break;

        case 'declined':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.Declined;
          break;

        case 'requested':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.Requested;
          break;

        case 'queue':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.Queue;
          break;

        case 'by-channel':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.ByChannel;
          break;

        case 'completed':
          this.selectedEmployeeStatusTab = EmployeeStatusTabsModel.Completed;
          break;
      }
    });

    const { fontFamily, fontSize } = 
      this.tenantContext.getCertificateFontSettings();
    this.certificateFontFamily = fontFamily;
    this.certificateFontSize = fontSize;
    
    const formData = this.loadingService
      .load(
        forkJoin({
          trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
          trainingCategories:
            this.trainingCategoryService.getAllTrainingCategories(),
          organizationUnits:
            this.organisationUnitService.getAllOrganisationUnits(),
          trainers: this.personService.getAllPersons(),
          externalContacts: this.externalContactService.getAllContacts(),
          templates: this.trainingTemplateService.getAllTrainingTemplates(),
          integrations: this.integrationsService.getIntegrations(),
          questionTemplates: this.questionTemplateService.getQuestionTemplates(),
          isWbdConfigured: this.userService.getWbdConfigured(),
          tenantSubscription: this.tenantSubscriptionService.getCurrentTenantSubscription(),
          personCount: this.personService.getPersonCount(),
          standardTrainingCategories:
            this.standardTrainingCategoryService.getAllStandardTrainingCategories(
              this.userContext.currentPersonCulture ?? 'en'
            ),
          usedStandardTrainingCategories: 
            this.trainingService.getUsedStandardTrainingCategories(),
          roles: this.roleService.getAllRoles(),
          subscribers: this.subscriptionService.getApprovedTenantSubscribers()
        })
      )
      .pipe(shareReplay(1));

    formData.subscribe(
      ({
        trainingGroups,
        trainingCategories,
        organizationUnits,
        trainers,
        externalContacts,
        templates,
        integrations,
        questionTemplates,
        isWbdConfigured,
        tenantSubscription,
        personCount,
        standardTrainingCategories,
        usedStandardTrainingCategories,
        roles,
        subscribers
      }) => {
        this.trainingGroups = trainingGroups;
        this.trainingCategories = trainingCategories;
        this.templates = templates;
        this.organizationUnits = organizationUnits;
        this.questionTemplates = questionTemplates;
        this.isWbdConfigured = isWbdConfigured;
        this.integrations = integrations;
        this.isTenantPublishedToSubscribers = 
          tenantSubscription?.publishToSubscribers ?? false;
        this.isPrivatePublishing =
          tenantSubscription?.publishToSubscribers === true &&
          tenantSubscription?.isPrivatePublishing === true;

        this.personCount = personCount;
        this.roles = roles;
        this.subscribers = subscribers; 

        this.standardTrainingCategories = standardTrainingCategories.map(x => ({
          id: x.id,
          name: x.name ?? '',
          description: x.description ?? '',
          descriptionExtended: x.descriptionExtended ?? '',
          type: x.type,
          parentId: x.parentId,
          imageFile: x.imageFile
        }));

         this.usedStandardTrainingCategories = usedStandardTrainingCategories.map(x => ({
          id: x.id,
          name: x.name ?? '',
          description: x.description ?? '',
          descriptionExtended: x.descriptionExtended ?? '',
          type: x.type,
          parentId: x.parentId,
          imageFile: x.imageFile
        }));

        this.locations = organizationUnits
          .filter(
            (organizationUnit) =>
              organizationUnit.type == OrganisationUnitTypeModel.Location
          )
          .map((location) => ({ id: location.id, name: location.name }));

        this.departments = organizationUnits
          .filter(
            (organizationUnit) =>
              organizationUnit.type == OrganisationUnitTypeModel.Department
          )
          .map((department) => ({ id: department.id, name: department.name }));

        this.certificateOrganizationUnits = this.organisationUnitService
          .buildCertificateTree(organizationUnits).map(u => ({
            ...u,
            selected: u.id === this.trainingForm.certificateFooterLocationId
          }));

        this.trainers = trainers.map((trainer) => ({
          id: trainer.id,
          name: `${trainer.firstName} ${trainer.lastName}`,
        }));

        externalContacts.forEach((contact) => {
          this.trainers.push({
            id: contact.id,
            name: `${contact.firstName} ${contact.lastName}`,
          });
        });

        this.setDropdownOptions(integrations);

        if (this.editingTrainingId) {
          const trainingAndMembers = this.loadingService
            .load(
              trainingService.getTrainingView(this.editingTrainingId).pipe(
                switchMap((trainingView) => {
                  const training = trainingView.training;
                  this.training = training;
                  this.displayEventKey = trainingView.displayEventKey;
                  this.patchForm(training);

                  return forkJoin({
                    training: of(training),
                    members: trainingService.getEventMembers(this.editingTrainingId),
                    completedMembers:
                      training.status === 2
                        ? completedEventService.getEventCompletedMembers(this.editingTrainingId)
                        : of([]),
                    channels: !this.training.isFromSubscribedSource
                      ? this.trainingService.getTrainingChannelsStatistics(this.editingTrainingId)
                      : of([])
                  });
                }),
                shareReplay(1)
              )
            );

          trainingAndMembers.subscribe(({ training, members, completedMembers, channels }) => {
            this.allMembers = members;
            this.openMembers = members.filter(
              (m) => m.status == EventMemberStatusModel.Open
            );
            this.acceptedMembers = members.filter(
              (m) => m.status == EventMemberStatusModel.Accepted
            );
            this.declinedMembers = members.filter(
              (m) => m.status == EventMemberStatusModel.Declined
            );
            this.requestedMembers = members.filter(
              (m) => m.status == EventMemberStatusModel.Requested
            );
            this.queueMembers = members.filter(
              (m) => m.status == EventMemberStatusModel.Waiting
            );

            this.channels = channels;

            this.completedMembers = (completedMembers ?? []).map((person: PersonModel) => {
              return {
                person,
                status: 0
              }
            }) as unknown as EventMemberModel[];

            if(this.selectedEmployeeStatusTab !== null) {
              this.showEmployeeModal(this.selectedEmployeeStatusTab);
            }

            this.authorizationService
              .getCurrentPerson()
              .pipe(first())
              .subscribe((currentUser) => {
                const isTrainer = currentUser!.person.role.name === 'Trainer';
                const isTrainerAssigned = this.training?.trainers.some(
                  (trainer) => trainer.id === currentUser.person.id
                );
                const isCreator = this.training!.training.creatorId === currentUser.person.id;

                if (isTrainer && isTrainerAssigned && !isCreator) {
                  this.primaryButton = this.trainingHelperService.getPrimaryButtonType(
                    training,
                    true
                  );
                  this.buttonTypes = this.trainingHelperService.getSecondaryButtonTypes(
                    training,
                    members,
                    true
                  );
                } else {
                  this.primaryButton = this.trainingHelperService.getPrimaryButtonType(training);
                  this.buttonTypes = this.trainingHelperService.getSecondaryButtonTypes(
                    training,
                    members,
                  );
                }

                this.menuActions = this.menuActions.filter((item) =>
                  this.buttonTypes!.includes(item.value)
                );
              });
          });
        }
      }
    );
  }

  onLoadMembers() {
    if(!this.training) return;
    
    const data = this.loadingService
      .load(
        forkJoin({
          members: this.trainingService.getEventMembers(this.editingTrainingId),
          channels: !this.training.isFromSubscribedSource
            ? this.trainingService.getTrainingChannelsStatistics(this.editingTrainingId)
            : of([])
        })
      )
      .pipe(shareReplay(1));

    data.subscribe(({ members, channels }) => {
      this.openMembers = members.filter(
        (m) => m.status == EventMemberStatusModel.Open
      );
      this.acceptedMembers = members.filter(
        (m) => m.status == EventMemberStatusModel.Accepted
      );
      this.declinedMembers = members.filter(
        (m) => m.status == EventMemberStatusModel.Declined
      );
      this.requestedMembers = members.filter(
        (m) => m.status == EventMemberStatusModel.Requested
      );
      this.queueMembers = members.filter(
        (m) => m.status == EventMemberStatusModel.Waiting
      );

      this.channels = channels;

      setTimeout(() => {
        this.trainingEmployeeModal.reloadPersonsCount();
      });
    });
  }

  ngOnInit(): void {
    this.googleAutocompleteResults$.subscribe((results) => {
      this.googlePlaces = results;
    });
  }

  navigateBack(): void {
    if (this.isViewMode || !this.editingTrainingId) {
      this.pageStateService.setFlag(
        PageStateFlags.RestoreTrainingsOverviewTable
      );

      this.router.navigate(['/training']);
    } else {
      this.router.navigate([
        '/training/' + this.editingTrainingId
      ]);
    }
  }

  navigateViewMode(id: string): void {
    this.router.navigate(['/training/' + id]);
  }

  onFormSubmitClick(): void {
    let isFormValid = this.isFormValid();
    if(!isFormValid) return;

    if(
      this.editingTrainingId && 
      this.acceptedMembers.length !== 0 &&
      this.hasStartDateChanged
    ) { 
      this.showStartDateChangedNotificationModal = true;
      return;
    }

    this.submitForm();
  }

  submitForm(): void {
    const questions = this.participationQuestionEditorComponent
      ? this.participationQuestionEditorComponent.getQuestions()
      : null;

    this.adjustTargetGroupBeforeSubmit();

    this.trainingForm.organisationUnitLocationIds = [];

    if (!this.selectedOrganizationUnitIds.length) {
      this.organizationUnits.forEach((unit) => {
        this.trainingForm.organisationUnitLocationIds.push(unit.id);
      });
    } else {
      this.selectedOrganizationUnitIds.forEach((id) => {
        this.trainingForm.organisationUnitLocationIds.push(id);
      });
    }

    if (this.editingTrainingId) {
      const training = {
        ...this.trainingForm,
        dates: this.trainingDateSelectionComponent.getDates(),
        imageBase64: this.trainingForm.imageBase64
          ? this.trainingForm.imageBase64!.split(',')[1]
          : null,
        questions: questions
      };

      if (this.trainingForm.trainingType === 0) {
        training.iddTimeMinutes =
          this.trainingForm.iddHours! * 60 + this.trainingForm.iddMinutes!;
      } else {
        training.iddTimeMinutes = 0;
        training.lernart = null;
        training.lerninhalt = null;
      }

      const result = this.trainingService.updateTraining(
        this.editingTrainingId,
        training
      );

      this.loadingService.load(result
        .pipe(
          switchMap(res => {
            if (
              this.hasStartDateChanged &&
              this.startDateChangedNotificationResult && 
              this.startDateChangedNotificationResult.notifyAcceptedMembers
            ) {
              return this.trainingService.notifyTrainingStartDateChanged(this.editingTrainingId, {
                notificationInformationText: this.startDateChangedNotificationResult.notificationInformationText,
              })
            }

            return of(null);
          })
        )
      ).subscribe({
        next: () => {
          this.toastrService.success('Training successfully updated!');
          this.navigateViewMode(this.editingTrainingId);
        },
        error: () => this.toastrService.error('Training update error!'),
      });
    } else {
      const training: TrainingCreationAndUpdateModel = {
        ...this.trainingForm,
        dates: this.trainingDateSelectionComponent.getDates(),
        imageBase64: this.trainingForm.imageBase64
          ? this.trainingForm.imageBase64!.split(',')[1]
          : null,
        questions: questions
      };

      if (!training.imageBase64) {
        delete training.imageBase64;
      }

      if (this.trainingForm.trainingType === 0) {
        training.iddTimeMinutes =
          this.trainingForm.iddHours! * 60 + this.trainingForm.iddMinutes!;
      } else {
        training.iddTimeMinutes = 0;
        training.lernart = null;
        training.lerninhalt = null;
      }

      const result = this.trainingService.addTraining(training);
      this.loadingService.load(result).subscribe({
        next: (training: TrainingFullModel) => {
          this.toastrService.success('Training successfully created!');
          this.navigateViewMode(training.training.id);
        },
        error: () => this.toastrService.error('Training creation error!'),
      });
    }
  }

  private adjustTargetGroupBeforeSubmit(): void {
    if (
      this.trainingForm.publishingMode === PublishingMode.ByInvitationOnly
    ) {
      this.selectedOrganizationUnitIds = [];
      this.trainingForm.trainingGroupIds = [];
      this.trainingForm.trainingCategoryIds = [];
      this.trainingForm.standardTrainingCategoryIds = [];
      this.trainingForm.roleIds = [];
      this.trainingForm.subscriptionIds = [];
      this.trainingForm.allowSharingWithSubscribers = false;
    } else if (
      this.trainingForm.publishingMode === PublishingMode.PublishToCatalogue
    ) {
      this.selectedOrganizationUnitIds = [];
      this.trainingForm.trainingGroupIds = [];
      this.trainingForm.roleIds = [];
      this.trainingForm.standardTrainingCategoryIds = [];
      this.trainingForm.subscriptionIds = [];
      this.trainingForm.allowSharingWithSubscribers = false;
    }

    if (
      this.trainingForm.visibilityMode === EventVisibilityModeModel.Everyone
    ) {
      this.selectedOrganizationUnitIds = [];
      this.trainingForm.trainingGroupIds = [];
      this.trainingForm.roleIds = [];
    }

    if (
      this.trainingForm.visibilityMode === EventVisibilityModeModel.Subscribers
    ) {
      this.trainingForm.trainingCategoryIds = [];
      this.selectedOrganizationUnitIds = [];
      this.trainingForm.trainingGroupIds = [];
      this.trainingForm.roleIds = [];
    }

    if (
      this.trainingForm.visibilityMode !== EventVisibilityModeModel.Subscribers &&
      !this.trainingForm.allowSharingWithSubscribers
    ) {
      this.trainingForm.standardTrainingCategoryIds = [];
      this.trainingForm.subscriptionIds = [];
    }

    if (this.isExternalEvent) {
      this.trainingForm.maximumMembers = null;
      this.trainingForm.reminders = [];
      this.trainingForm.sendCertificateLink = false;
      this.trainingForm.sendFeedbackLink = false;
      this.trainingForm.allowUnknownUsersToJoin = false;
    }
  }

  isFormValid(): boolean {
    this.isTrainingLocationValid = true;

    const descriptionResult = this.descriptionForm.instance.validate();
    if (!descriptionResult.isValid) {
      this.scrollDxComponent(this.descriptionForm);
      return false;
    }

    const settingsResult = this.settingsForm.instance.validate();
    if (!settingsResult.isValid) {
      this.scrollDxComponent(this.settingsForm);
      return false;
    }

    const locationResult = this.locationForm.instance.validate();
    if (!locationResult.isValid) {
      this.scrollDxComponent(this.locationForm);
      return false;
    }

    const group = validationEngine.getGroupConfig('customFields');

    if (group) {
      const customFieldsResult = validationEngine.validateGroup('customFields');

      if (!customFieldsResult.isValid) {
        this.scrollToFirstBrokenRule(customFieldsResult);
        return false;
      }
    }

    const trainingTimeResult =
      this.trainingForm.trainingType !== TrainingTypeModel.IDD
        ? { isValid: true }
        : validationEngine.validateGroup('trainingTimeGroup');

    this.isTrainingTimeValid = !!trainingTimeResult.isValid;

    if (!trainingTimeResult.isValid) {
      this.timePicker?.nativeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return false;
    }

    if (
      this.trainingForm.trainingLocation.locationType ===
      TrainingLocationTypeModel.Address
    ) {
      if (
        (!this.trainingForm.trainingLocation.latitude ||
        !this.trainingForm.trainingLocation.longitude)
      ) {
        this.trainingForm.trainingLocation.location = this.googleSearchText;
      }

      const locationValidationResult =
        validationEngine.validateGroup('trainingLocationGroup');

      this.isTrainingLocationValid = !!locationValidationResult.isValid;

      if (!this.isTrainingLocationValid) {
        this.googleAutocomplete?.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        return false;
      }
    }

    if (!this.trainingDateSelectionComponent.isValid()) {
      return false;
    }

    if(
      this.participationQuestionEditorComponent &&
      !this.participationQuestionEditorComponent.validate()
    ) {
      return false;
    }

    return true;
  }

  private scrollDxComponent(component: any): void {
    const element = component?.instance?.element?.();
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  private scrollToFirstBrokenRule(result: any): void {
    if (!result?.brokenRules?.length) return;

    const firstBrokenRule = result.brokenRules[0] as any;
    const element = firstBrokenRule?.validator?.$element?.();

    element?.[0]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    element?.[0]?.focus();
  }

  setDropdownOptions(integrations: IntegrationModel[]) {
    const defaultSelectOptions = {
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;

    this.trainingTypeOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        {
          id: 0,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.IDD'
          ),
        },
        {
          id: 1,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
          ),
        },
        {
          id: 2,
          name: this.translateService.instant(
            'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
          ),
        },
      ],
      placeholder: '',
    };

    this.learningTypeOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        { id: 1, name: this.translateService.instant('COMMON.FIELDS.LEARNING-TYPES.FACE-TO-FACE') },
        { id: 2, name: this.translateService.instant('COMMON.FIELDS.LEARNING-TYPES.INDIVIDUAL-TRAINING') },
        { id: 3, name: this.translateService.instant('COMMON.FIELDS.LEARNING-TYPES.BLENDED-LEARNING') },
        { id: 4, name: this.translateService.instant('COMMON.FIELDS.LEARNING-TYPES.CONTROLLED-ELEARNING') },
        { id: 5, name: this.translateService.instant('COMMON.FIELDS.LEARNING-TYPES.SELF-DIRECTED-ELEARNING') },
        { id: 6, name: this.translateService.instant('COMMON.FIELDS.LEARNING-TYPES.LEARNING-AT-WORK') },
      ],
      placeholder: '',
    };

    this.learningContentOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        { id: 1, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.PENSION-INSURANCE') },
        { id: 2, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.NURSING-INSURANCE') },
        { id: 3, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.DAMAGE-INSURANCE') },
        { id: 4, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.CUSTOMER-PENSION-PROVISION') },
        { id: 5, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.CUSTOMER-PROPERTY') },
        { id: 6, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.CROSS-DIVISIONAL') },
        { id: 7, name: this.translateService.instant('COMMON.FIELDS.LEARING-CONTENT-TYPES.ADVISORY-COMPETENCE') },
      ],
      placeholder: '',
    };

    this.setLocationTypeOptions(true);
  }

  setLocationTypeOptions(isIntegrationsAvailable: boolean) {
    const defaultSelectOptions = {
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;

    const trainingLocationTypeOptionsDataSource = [
      {
        id: TrainingLocationTypeModel.Address,
        name: this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.FACE-TO-FACE'
        ),
      },
      {
        id: TrainingLocationTypeModel.Web,
        name: this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.ONLINE'
        ),
      },
      {
        id: TrainingLocationTypeModel.ExternalEvent,
        name: this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.EXTERNAL-EVENT'
        ),
      },
    ];

    if (isIntegrationsAvailable) {
      for (let integration of this.integrations) {
        if (integration.type == IntegrationTypeModel.GoToWebinar) {
          trainingLocationTypeOptionsDataSource.push({
            id: TrainingLocationTypeModel.GoToWebinar,
            name: this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.GO-TO-WEBINAR'
            ),
          });
        }
        if (integration.type == IntegrationTypeModel.Webex) {
          trainingLocationTypeOptionsDataSource.push({
            id: TrainingLocationTypeModel.Webex,
            name: this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.WEBEX-WEBINAR'
            ),
          });
        }
        if (integration.type == IntegrationTypeModel.MsTeams) {
          trainingLocationTypeOptionsDataSource.push({
            id: TrainingLocationTypeModel.MsTeamsWebinar,
            name: this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.MSTEAMS'
            ),
          });
        }
        if (integration.type == IntegrationTypeModel.MsTeamsMeetings) {
          trainingLocationTypeOptionsDataSource.push({
            id: TrainingLocationTypeModel.MsTeamsMeetings,
            name: 'Teams Meeting'
            // name: this.translateService.instant(
            //   'COMMON.FIELDS.EVENT-TYPES.MSTEAMS'
            // ),
          });
        }
        if (integration.type == IntegrationTypeModel.Zoom) {
          trainingLocationTypeOptionsDataSource.push({
            id: TrainingLocationTypeModel.Zoom,
            name: this.translateService.instant(
              'COMMON.FIELDS.EVENT-TYPES.ZOOM'
            ),
          });
        }
      }
    }
    this.trainingLocationTypeOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      onItemClick: this.onTrainingLocationTypeSelected.bind(this),
      dataSource: trainingLocationTypeOptionsDataSource,
      placeholder: '',
    };

    if (this.editingTrainingId) {
      this.trainingLocationTypeOptions.disabled = true;
    }
  }

  setTreeViewValues() {
    this.trainingGroups.forEach((trainingGroup) => {
      if (this.trainingForm.trainingGroupIds.indexOf(trainingGroup.id) > -1) {
        trainingGroup.selected = true;
      }
    });

    this.trainingCategories.forEach((trainingCategory) => {
      if (
        this.trainingForm.trainingCategoryIds.indexOf(trainingCategory.id) > -1
      ) {
        trainingCategory.selected = true;
      }
    });

    this.standardTrainingCategories.forEach((trainingCategory) => {
      if (
        this.trainingForm.standardTrainingCategoryIds.indexOf(trainingCategory.id) > -1
      ) {
        trainingCategory.selected = true;
      }
    });

    this.usedStandardTrainingCategories.forEach((trainingCategory) => {
      if (
        this.trainingForm.standardTrainingCategoryIds.indexOf(trainingCategory.id) > -1
      ) {
        trainingCategory.selected = true;
      }
    });

    this.roles.forEach((role) => {
      if (
        this.trainingForm.roleIds.indexOf(role.id) > -1
      ) {
        role.selected = true;
      }
    });

    this.subscribers.forEach((subscriber) => {
      if (
        this.trainingForm.subscriptionIds.indexOf(subscriber.subscriptionId) > -1
      ) {
        subscriber.selected = true;
      }
    });

    this.organizationUnits.forEach((unit) => {
      if (this.selectedOrganizationUnitIds.indexOf(unit.id) > -1) {
        unit.selected = true;
      }
    });

    this.reminderOptions.forEach((reminder) => {
      if (this.trainingForm.reminders.indexOf(reminder.value) > -1) {
        reminder.selected = true;
      }
    });
    // this.locations.forEach((location) => {
    //   if (
    //     this.trainingForm.organisationUnitLocationIds.indexOf(location.id) > -1
    //   ) {
    //     location.selected = true;
    //   }
    // });

    // this.departments.forEach((department) => {
    //   if (
    //     this.trainingForm.organisationUnitDepartmentIds.indexOf(department.id) >
    //     -1
    //   ) {
    //     department.selected = true;
    //   }
    // });

    this.trainers.forEach((trainer) => {
      if (this.trainingForm.trainerIds.indexOf(trainer.id) > -1) {
        trainer.selected = true;
      }
    });
  }

  copyTrainingKey(key: string) {
    if (!key) return;

    if (this.clipboard.copy(key)) {
      this.toastrService.success(
        this.translateService.instant(
          "TRAINING.FORM.TRAINING-KEY-SAVED-TO-CLICKBOARD"
        )
      );
    } else {
      this.toastrService.warning(
          this.translateService.instant(
          "TRAINING.FORM.TRAINING-KEY-NOT-SAVED-TO-CLICKBOARD"
        )
      );
    }
  }

  onTrainingLocationTypeSelected() {
    this.trainingForm.trainingLocation.location = '';
    this.trainingForm.trainingLocation.suffix = '';
    this.trainingForm.trainingLocation.placeId = '';
    this.trainingForm.trainingLocation.latitude = null;
    this.trainingForm.trainingLocation.longitude = null;
    this.trainingForm.trainingLocation.calendarEventId = null;
    this.trainingForm.questions = null;
    this.googleSearchText = '';

    if (this.isExternalEvent) {
      this.trainingForm.trainerIds = [];
    }
  }

  onTrainingTemplateSelected(e: any) {
    this.trainingForm.templateId = e.itemData.id;
    this.loadingService
      .load(this.trainingTemplateService.getTrainingTemplate(e.itemData.id))
      .subscribe((trainingTemplateFull) => {
        this.selectedOrganizationUnitIds =
          trainingTemplateFull.organisationUnits.map((unit) => unit.id);
        // this.trainingForm.organisationUnitLocationIds =
        //   trainingTemplateFull.organisationUnits
        //     .filter(
        //       (organizationUnit) =>
        //         organizationUnit.type == OrganisationUnitTypeModel.Location
        //     )
        //     .map((organizationUnit) => organizationUnit.id);
        // this.trainingForm.organisationUnitDepartmentIds =
        //   trainingTemplateFull.organisationUnits
        //     .filter(
        //       (organizationUnit) =>
        //         organizationUnit.type == OrganisationUnitTypeModel.Department
        //     )
        //     .map((organizationUnit) => organizationUnit.id);
        this.trainingForm.trainerIds = trainingTemplateFull.trainers.map(
          (trainer) => trainer.id
        );
        this.trainingForm.trainingGroupIds =
          trainingTemplateFull.trainingGroups.map(
            (trainingGroup) => trainingGroup.id
          );

        this.trainingForm.trainingCategoryIds =
          trainingTemplateFull.trainingCategories.map(
            (trainingCategory) => trainingCategory.id
          );

        this.trainingForm.standardTrainingCategoryIds =
          trainingTemplateFull.standardTrainingCategories.map(
            (trainingCategory) => trainingCategory.id
          );

        this.trainingForm.roleIds =
          trainingTemplateFull.roles.map(
            (role) => role.id
          );

        this.trainingForm.subscriptionIds =
          trainingTemplateFull.subscriptions.map(
            (subscription) => subscription.id
          );

        this.trainingForm.reminders = [...trainingTemplateFull.reminders];
        this.trainingForm.name = trainingTemplateFull.trainingTemplate.name;
        this.trainingForm.description =
          trainingTemplateFull.trainingTemplate.description!;
        this.trainingForm.descriptionExtended =
          trainingTemplateFull.trainingTemplate.descriptionExtended!;
        this.trainingForm.descriptionCertificate =
          trainingTemplateFull.trainingTemplate.descriptionCertificate!;
        this.trainingForm.trainingType =
          trainingTemplateFull.trainingTemplate.trainingType;
        this.trainingForm.sendCertificateLink =
          trainingTemplateFull.trainingTemplate.sendCertificateLink;
        this.trainingForm.sendFeedbackLink =
          trainingTemplateFull.trainingTemplate.sendFeedbackLink;
        this.trainingForm.allowUnknownUsersToJoin =
          trainingTemplateFull.trainingTemplate.allowUnknownUsersToJoin;
        this.trainingForm.allowSharingWithSubscribers =
          trainingTemplateFull.trainingTemplate.allowSharingWithSubscribers;
        this.trainingForm.maximumMembers =
          trainingTemplateFull.trainingTemplate.maximumMembers;
        this.trainingForm.emailBody =
          trainingTemplateFull.trainingTemplate.emailBody;
        this.trainingForm.iddTimeMinutes =
          trainingTemplateFull.trainingTemplate.iddTimeMinutes;
        this.trainingForm.certificateFooterLocationId =
          trainingTemplateFull.trainingTemplate.certificateFooterLocationId;
        this.trainingForm.iddHours = TimeService.getHoursFromAbsoluteMinutes(
          this.trainingForm.iddTimeMinutes || 0
        );
        this.trainingForm.publishingMode =
          trainingTemplateFull.trainingTemplate.publishingMode;
        this.trainingForm.visibilityMode =
          trainingTemplateFull.trainingTemplate.visibilityMode;
        this.trainingForm.lernart =
          trainingTemplateFull.trainingTemplate.lernart;
        this.trainingForm.lerninhalt =
          trainingTemplateFull.trainingTemplate.lerninhalt;
        this.trainingForm.iddMinutes =
          TimeService.getMinutesFromAbsoluteMinutes(
            this.trainingForm.iddTimeMinutes || 0
          );

        if (trainingTemplateFull.trainingTemplate.trainingLocation != null) {
          this.trainingForm.trainingLocation.locationType =
            trainingTemplateFull.trainingTemplate.trainingLocation.type;
          this.trainingForm.trainingLocation.location =
            trainingTemplateFull.trainingTemplate.trainingLocation.location;
          this.trainingForm.trainingLocation.suffix =
            trainingTemplateFull.trainingTemplate.trainingLocation.suffix!;
          this.trainingForm.trainingLocation.placeId =
            trainingTemplateFull.trainingTemplate.trainingLocation.placeId!;
          this.trainingForm.trainingLocation.calendarEventId =
            trainingTemplateFull.trainingTemplate.trainingLocation.calendarEventId ?? null;
          this.trainingForm.trainingLocation.latitude =
            trainingTemplateFull.trainingTemplate.trainingLocation.latitude!;
          this.trainingForm.trainingLocation.longitude =
            trainingTemplateFull.trainingTemplate.trainingLocation.longitude!;
          this.googleSearchText =
            trainingTemplateFull.trainingTemplate.trainingLocation.location;
        }

        if(trainingTemplateFull.questions) {
          this.trainingForm.questions = trainingTemplateFull.questions.map(q => ({
            id: null,
            question: q.question,
            translationKey: q.translationKey,
            type: q.type,
            isRequired: q.isRequired,
            templateId: q.templateId,
            maxAdditionalPersons: q.maxAdditionalPersons,
            startHotelDate: q.startHotelDate,
            endHotelDate: q.endHotelDate,
            options: q.options?.map(o => ({
              id: null,
              value: o.value,
              translationKey: o.translationKey,
            })) ?? null
          }));
        }

        const imageUrl = trainingTemplateFull.trainingTemplate.imageFile?.fileUrl;

        if (imageUrl) {
          this.loadingService
            .load(
              this.fileUtilService.avatarUrlToBase64(imageUrl)
            )
            .subscribe((base64) => {
              const mimeType = this.fileUtilService.getImageMimeType(imageUrl);
              this.trainingForm.imageBase64 = `data:${mimeType};base64,${base64}`;
            });
        }

        this.setTreeViewValues();
      });
  }


  onTrainerSelected(e: any) {
    if (e.node.selected) {
      this.trainingForm.trainerIds = [
        ...this.trainingForm.trainerIds,
        e.node.key,
      ];
    } else {
      this.trainingForm.trainerIds = this.trainingForm.trainerIds.filter(
        (id) => id !== e.node.key
      );
    }
  }

  onAvatarUploaded(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.trainingForm!.imageBase64 = reader!.result!.toString();
    };
    reader.readAsDataURL(file);
  }

  handleTimePicker(value: { hours: number; minutes: number }) {
    this.trainingForm.iddHours = value.hours;
    this.trainingForm.iddMinutes = value.minutes;

    const trainingTimeGroupConfig =
      validationEngine.getGroupConfig('trainingTimeGroup');
    if (this.trainingForm.trainingType === TrainingTypeModel.IDD && trainingTimeGroupConfig) {
      const isValid =
        validationEngine.validateGroup('trainingTimeGroup').isValid;

      if (isValid) {
        this.isTrainingTimeValid = true;
      }
    }
  }

  patchForm(training: TrainingFullModel) {
    if (
      training.organisationUnits.length === this.organizationUnits.length &&
      this.isViewMode
    ) {
      this.selectedOrganizationUnitIds = ['0'];
      // @ts-ignore
      this.organizationUnits = [
        {
          id: '0',
          name: this.translateService.instant('COMMON.ALL'),
          parentId: null,
          type: 1,
          description: '',
          reportFooter: '',
          imageFile: null,
          topLevel: true,
        },
      ];
    } else {
      this.selectedOrganizationUnitIds = training.organisationUnits.map(
        (unit) => unit.id
      );
    }
    // this.trainingForm.organisationUnitLocationIds = training.organisationUnits
    //   .filter(
    //     (organizationUnit) =>
    //       organizationUnit.type == OrganisationUnitTypeModel.Location
    //   )
    //   .map((organizationUnit) => organizationUnit.id);
    // this.trainingForm.organisationUnitDepartmentIds = training.organisationUnits
    //   .filter(
    //     (organizationUnit) =>
    //       organizationUnit.type == OrganisationUnitTypeModel.Department
    //   )
    //   .map((organizationUnit) => organizationUnit.id);
    this.trainingForm.templateId = training.training.templateId;
    this.trainingForm.name = training.training.name;
    this.trainingForm.trainingType = training.training.trainingType;
    this.trainingForm.description = training.training.description!;
    this.trainingForm.descriptionExtended =
      training.training.descriptionExtended!;
    this.trainingForm.descriptionCertificate =
      training.training.descriptionCertificate!;
    this.trainingForm.trainerIds = training.trainers.map(
      (trainer) => trainer.id
    );
    this.trainingForm.trainingGroupIds = training.trainingGroups.map(
      (trainingGroup) => trainingGroup.id
    );
    this.trainingForm.trainingCategoryIds = training.trainingCategories.map(
      (trainingCategory) => trainingCategory.id
    );
    this.trainingForm.standardTrainingCategoryIds = training.standardTrainingCategories.map(
      (trainingCategory) => trainingCategory.id
    );
    this.trainingForm.roleIds = training.roles.map(
      (role) => role.id
    );
    this.trainingForm.subscriptionIds = training.subscriptions.map(
      (subscription) => subscription.id
    );
    this.trainingForm.sendCertificateLink =
      training.training.sendCertificateLink;
    this.trainingForm.sendFeedbackLink = training.training.sendFeedbackLink;
    this.trainingForm.publishingMode = training.training.publishingMode;
    this.trainingForm.visibilityMode = training.training.visibilityMode;
    this.trainingForm.maximumMembers = training.training.maximumMembers;
    this.trainingForm.emailBody = training.training.emailBody;
    this.trainingForm.iddTimeMinutes = training.training.iddTimeMinutes;
    this.trainingForm.trainingLocation.locationType =
      training.training.trainingLocation.type;
    this.googleSearchText = training.training.trainingLocation.location;
    this.trainingForm.trainingLocation.location =
      training.training.trainingLocation.location;
    this.trainingForm.trainingLocation.suffix =
      training.training.trainingLocation.suffix!;
    this.trainingForm.trainingLocation.placeId =
      training.training.trainingLocation.placeId!;
    this.trainingForm.trainingLocation.calendarEventId =
      training.training.trainingLocation.calendarEventId ?? null;
    this.trainingForm.trainingLocation.latitude =
      training.training.trainingLocation.latitude!;
    this.trainingForm.trainingLocation.longitude =
      training.training.trainingLocation.longitude!;
    this.trainingForm.lernart = training.training.lernart;
    this.trainingForm.lerninhalt = training.training.lerninhalt;
    this.trainingForm.isDeployed = training.training.isDeployed;
    this.trainingForm.allowUnknownUsersToJoin =
      training.training.allowUnknownUsersToJoin;
    this.trainingForm.allowSharingWithSubscribers =
      training.training.allowSharingWithSubscribers;
    this.trainingForm.certificateFooterLocationId =
      training.training.certificateFooterLocationId;
    this.trainingForm.reminders = [...training.reminders];

    if(training.questions) {
      this.trainingForm.questions = training.questions.map(q => ({
        id: q.id,
        question: q.question,
        translationKey: q.translationKey,
        type: q.type,
        isRequired: q.isRequired,
        templateId: q.templateId,
        maxAdditionalPersons: q.maxAdditionalPersons,
        startHotelDate: q.startHotelDate,
        endHotelDate: q.endHotelDate,
        options: q.options?.map(o => ({
          id: o.id,
          value: o.value,
          translationKey: o.translationKey,
        })) ?? null
      }));
    }

    this.trainingForm.iddHours = TimeService.getHoursFromAbsoluteMinutes(
      this.trainingForm.iddTimeMinutes || 0
    );
    this.trainingForm.iddMinutes = TimeService.getMinutesFromAbsoluteMinutes(
      this.trainingForm.iddTimeMinutes || 0
    );

    const imageUrl = training.training.imageFile?.fileUrl;

    if (imageUrl) {
      this.loadingService
        .load(
          this.fileUtilService.avatarUrlToBase64(imageUrl)
        )
        .subscribe((base64) => {
          const mimeType = this.fileUtilService.getImageMimeType(imageUrl);
          this.trainingForm.imageBase64 = `data:${mimeType};base64,${base64}`;
        });
    }
    if (!this.isViewMode) {
      this.trainingDateSelectionComponent.patchDates(training.dates);
    }

    this.setTreeViewValues();
  }

  onPublishingModeChanged(publishingMode: PublishingMode) {
    const hadVisibilityMode = this.trainingForm.visibilityMode != null;

    this.trainingForm.publishingMode = publishingMode;

    if (
      publishingMode === PublishingMode.DeployToTargetGroup
    ) {
      this.trainingForm.visibilityMode ??= EventVisibilityModeModel.Everyone;
    } else if (
      publishingMode === PublishingMode.PublishToSubscribers
    ) {
      this.trainingForm.visibilityMode ??= EventVisibilityModeModel.Subscribers;
    } else {
      this.trainingForm.visibilityMode = null;
    }

    if (
      publishingMode === PublishingMode.PublishToSubscribers &&
      !hadVisibilityMode &&
      this.trainingForm.subscriptionIds.length === 0
    ) {
      this.selectAllSubscriptions();
    }
  }

  private selectAllSubscriptions() {
    this.trainingForm.subscriptionIds = this.subscribers.map(
      subscriber => subscriber.subscriptionId
    );

    this.setTreeViewValues();
  }

  onVisibilityModeChanged(
    visibilityMode: EventVisibilityModeModel | null
  ): void {
    this.trainingForm.visibilityMode = visibilityMode;

    if (
      visibilityMode === EventVisibilityModeModel.Subscribers
    ) {
      this.trainingForm.allowUnknownUsersToJoin = true;
    }
  }

  onAllowSharingWithSubscribersChanged(
    allowSharingWithSubscribers: boolean
  ) {
    this.trainingForm.allowSharingWithSubscribers = 
      allowSharingWithSubscribers;

    if (allowSharingWithSubscribers) {
      this.trainingForm.allowUnknownUsersToJoin = true;
    } 
  }


  refreshComponent() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/training/edit/' + this.editingTrainingId]);
    });
  }

  getGooglePlaces(e: any) {
    this.isTrainingLocationValid = true;
    this.googlePlaceinputChanged.next(e.value);
  }

  setLocationPlace(e: any) {
    this.loadingService
      .load(this.mapsService.getDetails(e.itemData.place_id))
      .subscribe((details) => {
        const location = details.formatted_address.startsWith(details.name)
          ? details.formatted_address
          : details.name + ', ' + details.formatted_address;
        this.trainingForm.trainingLocation.location = location;
        this.trainingForm.trainingLocation.placeId = details.place_id;
        this.trainingForm.trainingLocation.calendarEventId = null;
        this.trainingForm.trainingLocation.latitude =
          details.geometry.location.lat;
        this.trainingForm.trainingLocation.longitude =
          details.geometry.location.lng;
        this.trainingForm.trainingLocation = {
          ...this.trainingForm.trainingLocation,
        };
      });
  }

  autocompleteSearchFilter(e: any) {
    return this.googleSearchText;
  }

  onMenuItemClick(event: DxDropDownButtonTypes.ItemClickEvent) {
    switch (event.itemData.value) {
      case SecondaryButtonType.CopyLink:
        this.onCopyLinkClick();
        break;
      case SecondaryButtonType.DownloadMemberList:
        this.onDownloadMemberListClick();
        break;
      case SecondaryButtonType.DownloadCompletedEventMemberList:
        this.onDownloadCompletedEventMemberListClick();
        break;
      case SecondaryButtonType.DownloadIcs:
        this.onDownloadIcsClick();
        break;
      case SecondaryButtonType.SendInfo:
        this.onSendInfoClick();
        break;
      case SecondaryButtonType.Edit:
        this.onEditClick();
        break;
      case SecondaryButtonType.Cancel:
        this.openCancelTraining();
        break;
      case SecondaryButtonType.Delete:
        this.openDeleteTrainingConfirmationModal();
        break;
      case SecondaryButtonType.AddAttendees:
        this.onInviteClick();
        break;
      case SecondaryButtonType.DownloadESignatureQRCode:
        this.onDownloadESignatureQRCode()
        break;
      case SecondaryButtonType.ActivityLog:
        this.onActivityLogClick()
        break;
      case SecondaryButtonType.CopyFeedbackLink:
        this.onDownloadFeedbackQRCode();
        break;
    }
  }

  onPrimaryButtonClick() {
    switch (this.primaryButton) {
      case PrimaryButtonType.AddAttendees:
        this.onInviteClick();
        break;
      case PrimaryButtonType.CompleteTraining:
        this.onCompleteClick();
        break;
      case PrimaryButtonType.ShowFeedback:
        this.onShowFeedbackClick();
        break;
      case PrimaryButtonType.DownloadCompletedEventMemberList:
        this.onDownloadCompletedEventMemberListClick();
        break;
      case PrimaryButtonType.DownloadMemberList:
        this.onDownloadMemberListClick();
        break;
      case PrimaryButtonType.DownloadESignatureQRCode:
        this.onDownloadESignatureQRCode()
        break;
      case PrimaryButtonType.Edit:
        this.onEditClick()
        break;
    }
  }

  onEditClick() {
    this.router.navigate(['training', 'edit', this.editingTrainingId]);
  }

  onInviteClick(): void {
    this.router.navigate(['training', 'invite', this.editingTrainingId]);
  }

  onCompleteClick(): void {
    this.router.navigate(['training', 'complete', this.editingTrainingId]);
  }

  onShowFeedbackClick(): void {
    this.router.navigate(['training', 'feedback', this.editingTrainingId]);
  }

  onActivityLogClick() {
    this.router.navigate(['activity-log'], {
      queryParams: { trainingId: this.editingTrainingId },
    });
  }

  onDownloadCompletedEventMemberListClick(): void {
    this.completedEventService.downloadCompletedEventMemberListByTrainingId(
      this.editingTrainingId
    );
  }

  onCopyLinkClick(): void {
    this.showCopyInvitationLinkModel = true;
  }

  esignatureQRCodeImageUrl?: string;
  showDownloadESignatureQRCodeModal: boolean = false;
  onDownloadESignatureQRCode(): void {
    if(!this.esignatureQRCodeImageUrl){
      this.trainingService.downloadESignatureQRCode(this.editingTrainingId)
      .subscribe(url => {
        if (url) {
          this.esignatureQRCodeImageUrl = url;
          this.showDownloadESignatureQRCodeModal = true;
        }
      });
    } else {
      this.showDownloadESignatureQRCodeModal = true;
    }
  }

  onCloseDownloadESignatureQRCodePopup() {
    this.showDownloadESignatureQRCodeModal = false;
  }

  feedbackQRCodeImageUrl?: string;
  showDownloadFeedbackQRCodeModal: boolean = false;
  onDownloadFeedbackQRCode(): void {
    if(!this.feedbackQRCodeImageUrl){
      this.trainingService.downloadFeedbackQRCode(this.editingTrainingId)
      .subscribe(url => {
        if (url) {
          this.feedbackQRCodeImageUrl = url;
          this.showDownloadFeedbackQRCodeModal = true;
        }
      });
    } else {
      this.showDownloadFeedbackQRCodeModal = true;
    }
  }

  onCloseDownloadFeedbackQRCodePopup() {
    this.showDownloadFeedbackQRCodeModal = false;
  }

  onDownloadMemberListClick(): void {
    const sub = this.loadingService
      .load(this.trainingService.getMemberListPdf(this.editingTrainingId))
      .subscribe({
        next: (pdf) => this.fileUtilService.downloadFile(pdf),
        error: () => this.toastrService.error('Es ist ein Fehler aufgetreten'),
      });
    this.subs.add(sub);
  }

  onDownloadIcsClick(): void {
    const sub = this.loadingService
      .load(this.trainingService.getIcs(this.editingTrainingId))
      .subscribe({
        next: (ics) => this.fileUtilService.downloadFile(ics),
        error: () => this.toastrService.error('Es ist ein Fehler aufgetreten'),
      });
    this.subs.add(sub);
  }

  onCopyFeedbackLinkClick(): void {
    if(!this.training) return;

    const base =
      window.location.origin + `/feedback/${this.training.training.key}`

    const url = new URL(base);

    if (this.clipboard.copy(url.toString())) {
      this.toastrService.success(
        this.translateService.instant(
          "TRAINING.FORM.FEEDBACK-LINK-SAVED-TO-CLICKBOARD"
        )
      );
    } else {
      this.toastrService.warning(
          this.translateService.instant(
          "TRAINING.FORM.FEEDBACK-LINK-NOT-SAVED-TO-CLICKBOARD"
        )
      );
    }
  }

  onSendInfoClick(): void {
    this.router.navigate(['training', 'message', this.editingTrainingId]);
  }

  openCancelTraining() {
    this.showCancelTrainingModal = true;
  }

  onCancelTrainingClick({
    cancelInformationText,
  }: {
    cancelInformationText: string | null;
  }): void {
    this.loadingService
      .load(
        this.trainingService.cancelTraining(this.editingTrainingId, {
          cancelInformationText,
        })
      )
      .subscribe({
        next: () => {
          this.toastrService.success('Schulung abgesagt');
          this.navigateBack();
        },
        error: () => {
          this.toastrService.error('Schulung konnte nicht abgesagt werden');
        },
      });
  }

  onStartDateChanged(newStartDate: DateTime) {
    if(!this.training) return;

    if(
      this.editingTrainingId && 
      this.acceptedMembers.length !== 0
    ) { 
      if(!this.hasStartDateChanged) {
        this.hasStartDateChanged = true;
      } else {
        const oldStartDate = this.training.dates
          .map(d => DateTime.fromISO(d.start))
          .reduce((min, curr) => (curr < min ? curr : min));

        const old = oldStartDate.startOf('minute');
        const newD = newStartDate.startOf('minute');

        if (old.equals(newD)) { 
          // start date reverted back to original value
          this.hasStartDateChanged = false;
        }
      }
    }
  }

  onStartDateChangedNotificationClick(
    result: TrainingStartDateChangedNotificationResult
  ): void {
    this.startDateChangedNotificationResult = result;
    this.showStartDateChangedNotificationModal = false;

    this.submitForm();
  }

  openDeleteTrainingConfirmationModal() {
    this.modalConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.YES'),
      message: this.translateService.instant(
        'TRAINING.DELETE-TRAINING-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
    };
    this.showDeleteTrainingModal = true;
  }

  onDeleteTrainingClick(): void {
    this.loadingService
      .load(this.trainingService.deleteTraining(this.editingTrainingId))
      .subscribe({
        next: () => {
          this.toastrService.success('Schulung gelöscht');
          this.navigateBack();
        },
        error: () => {
          this.toastrService.error('Schulung konnte nicht gelöscht werden');
        },
      });
  }

  showEmployeeModal(tab: number) {
    if(!this.training) {
      return;
    }

    let isAllowed = false;

    if (this.training!.status !== 2) {
      switch (tab) {
        case EmployeeStatusTabsModel.Accepted:
        case EmployeeStatusTabsModel.Open:
        case EmployeeStatusTabsModel.Declined:
        case EmployeeStatusTabsModel.ByChannel:
          isAllowed = true;
          break;

        case EmployeeStatusTabsModel.Requested:
          isAllowed = this.requestedMembers.length > 0;
          break;

        case EmployeeStatusTabsModel.Queue:
          isAllowed = this.queueMembers.length > 0;
          break;

        default:
          isAllowed = false;
          break;
      }
    } else {
      if (tab === EmployeeStatusTabsModel.Completed
        || tab === EmployeeStatusTabsModel.NoShows
      ) {
        isAllowed = true;
      }
    }

    if (!isAllowed) {
      return;
    }

    this.isEmployeePopupVisible = true;
    this.employeePopupTab = tab;
  }

  onEmployeePopupClose() {
    this.isEmployeePopupVisible = false;
  }

  areDatesOnSameDay(start: string, end: string) {
    return (
      new Date(start).toLocaleDateString() ===
      new Date(end).toLocaleDateString()
    );
  }

  validateTrainingTime(e: any) {
    return e.value  >= IDD_MIN_TRAINING_DURATION_MINUTES;
  }

  onFutureDateCheck(isAllInFuture: any) {
    if (isAllInFuture) {
    } else if (!isAllInFuture) {
      if (this.isIntegrationLocationType) {
        this.trainingForm.trainingLocation = {
          locationType: null,
          location: null,
          suffix: null,
          placeId: null,
          calendarEventId: null,
          latitude: null,
          longitude: null,
        };
      }
    }

    this.setLocationTypeOptions(isAllInFuture);
  }

  onDescriptionTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedDescriptionTab = e.itemData.id;
  }

  showCertificatePreview() {
    if(this.isViewMode && this.editingTrainingId) {
      const key: ReportKeyModel = {
        type: ReportKeyTypeModel.Event,
        id: this.editingTrainingId
      };

      const encoded = btoa(JSON.stringify(key));

      this.reportUrl = `CertificateReport:${encoded}`;
      this.showPreviewReportViewer = true;
    } else {
      let isFormValid = this.isFormValid();

      if(isFormValid) {
        const training: TrainingCreationAndUpdateModel = {
          ...this.trainingForm,
          dates: this.trainingDateSelectionComponent.getDates(),
          imageBase64: this.trainingForm.imageBase64
            ? this.trainingForm.imageBase64!.split(',')[1]
            : null,
        };

        if (this.trainingForm.trainingType === 0) {
          training.iddTimeMinutes =
            this.trainingForm.iddHours! * 60 + this.trainingForm.iddMinutes!;
        } else {
          training.iddTimeMinutes = 0;
          training.lernart = null;
          training.lerninhalt = null;
        }

        this.loadingService
          .load(
            this.reportPreviewService.createTrainingPreview(training)
          )
          .subscribe((res: PreviewResponse) => {
            const key: ReportKeyModel = {
              type: ReportKeyTypeModel.Preview,
              id: res.previewId
            };

            const encoded = btoa(JSON.stringify(key));

            this.reportUrl = `CertificateReport:${encoded}`;
            this.showPreviewReportViewer = true;
          })
      }
    }
  }

  onReminderSelectionChanged(e: any) {
    if (e.node.selected) {
      this.trainingForm.reminders = [
        ...this.trainingForm.reminders,
        e.node.key,
      ];
    } else {
      this.trainingForm.reminders =
        this.trainingForm.reminders.filter((id) => id !== e.node.key);
    }
  }

  onTemplateContentChanged(content: string) {
    this.trainingForm.descriptionExtended = content;
  }
}
